import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getAnchoredBoundPosition, getViewportBoundPosition, getEstimatedActionMenuHeight } from "../utils";

/**
 * Reusable action dropdown logic for table rows.
 *
 * @param {Object} opts
 * @param {React.RefObject} opts.tableScrollRef - ref to table scroll container (for boundary)
 * @param {number|Function} [opts.optionCount=3] - number of menu items, or (item) => number for dynamic count
 * @param {number} [opts.menuWidth=220] - dropdown menu width
 * @param {boolean} [opts.useAnchored=true] - use anchored positioning (true) or viewport (false)
 *
 * @returns {Object} { openActionId, openActionItem, dropdownPosition,
 *   dropdownRef, closeDropdown, handleDropdownAction, handleOpenDropdown }
 */
export function useActionDropdown({ tableScrollRef, optionCount = 3, menuWidth = 220, useAnchored = true } = {}) {
  const [openActionId, setOpenActionId] = useState(null);
  const [openActionItem, setOpenActionItem] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRef = useRef(null);
  const anchorRectRef = useRef(null);

  const closeDropdown = useCallback(() => {
    setOpenActionId(null);
    setOpenActionItem(null);
    setDropdownPosition(null);
    anchorRectRef.current = null;
  }, []);

  const computePosition = useCallback((anchorRect, menuW, menuH) => {
    const boundaryRect = tableScrollRef?.current?.getBoundingClientRect?.() || null;
    if (useAnchored && boundaryRect) {
      return getAnchoredBoundPosition(anchorRect, menuW, menuH, boundaryRect);
    }
    return getViewportBoundPosition(anchorRect, menuW, menuH);
  }, [useAnchored, tableScrollRef]);

  // After dropdown renders, recalculate position with actual dimensions
  useLayoutEffect(() => {
    if (!openActionId || !anchorRectRef.current) return;
    let rafId = requestAnimationFrame(() => {
      const el = dropdownRef.current;
      if (!el) return;
      const menuW = el.offsetWidth || menuWidth;
      const resolvedCount =
        typeof optionCount === "function"
          ? optionCount(openActionItem)
          : optionCount;
      const menuH = el.offsetHeight || getEstimatedActionMenuHeight(resolvedCount);
      if (!menuW || !menuH) return;
      const pos = computePosition(anchorRectRef.current, menuW, menuH);
      if (pos && typeof pos.left === "number" && typeof pos.top === "number") {
        setDropdownPosition({ left: pos.left, top: pos.top });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [openActionId, openActionItem, computePosition, menuWidth, optionCount]);

  // Click-outside close
  useEffect(() => {
    if (!openActionId) return;
    const onDocClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest(".dropdown-trigger-btn")) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openActionId, closeDropdown]);

  // Scroll-close: close dropdown when the table (or any ancestor) scrolls
  useEffect(() => {
    if (!openActionId) return;
    const scrollEl = tableScrollRef?.current;
    const onScroll = () => closeDropdown();
    if (scrollEl) scrollEl.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (scrollEl) scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
    };
  }, [openActionId, closeDropdown, tableScrollRef]);

  const handleDropdownAction = useCallback((fn) => {
    closeDropdown();
    if (typeof fn === "function") fn();
  }, [closeDropdown]);

  const handleOpenDropdown = useCallback((e, id, item) => {
    if (openActionId === id && openActionItem?.id === item.id) {
      closeDropdown();
      return;
    }
    const btn = e?.currentTarget;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      anchorRectRef.current = rect;
      const count = typeof optionCount === 'function' ? optionCount(item) : optionCount;
      const menuHeight = getEstimatedActionMenuHeight(count);
      const pos = computePosition(rect, menuWidth, menuHeight);
      setDropdownPosition(pos);
    }
    setOpenActionItem(item);
    setOpenActionId(id);
  }, [openActionId, openActionItem, closeDropdown, optionCount, menuWidth, computePosition]);

  return {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    closeDropdown,
    handleDropdownAction,
    handleOpenDropdown,
  };
}
